import { createAction, createReducer } from '@reduxjs/toolkit';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { type IAlbum, isAlbum } from 'reactApp/modules/albums/albums.types';
import { removeFileSuccess, renameItemSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { EStorageType } from 'reactApp/modules/storage/storage.types';

import { normalizeVigboAlbum, normalizeVigboAlbumItem } from './vigbo.helpers';
import {
    type CreateVigboAlbumRequestPayload,
    type CreateVigboAlbumsSuccessPayload,
    type LoadVigboAlbumsListSuccessPayload,
    type LoadVigboAlbumSuccessPayload,
    type SelectVigboAlbumAndAddItemsPayload,
    type VigboAlbumId,
    type VigboLinkState,
    type VigboState,
    VigboAccountLinkState,
} from './vigbo.types';

const initialState: VigboState = {
    isLoading: true,
    isLoaded: false,
    list: {},
    linkState: VigboAccountLinkState.unlinked,
};

export const loadVigboAlbumsListRequest = createAction('vigbo/loadVigboAlbumsListRequest');
export const loadVigboAlbumsListFail = createAction('vigbo/loadVigboAlbumsListFail');
export const loadVigboAlbumsListSuccess = createAction<LoadVigboAlbumsListSuccessPayload>('vigbo/loadVigboAlbumsListSuccess');

export const loadVigboAlbumRequest = createAction<VigboAlbumId>('vigbo/loadVigboAlbumRequest');
export const loadVigboAlbumSuccess = createAction<LoadVigboAlbumSuccessPayload>('vigbo/loadVigboAlbumSuccess');
export const loadVigboAlbumFail = createAction<VigboAlbumId>('vigbo/loadVigboAlbumFail');

export const createVigboAlbumRequest = createAction<CreateVigboAlbumRequestPayload>('vigbo/createVigboAlbumRequest');
export const createVigboAlbumSuccess = createAction<CreateVigboAlbumsSuccessPayload>('vigbo/createVigboAlbumSuccess');
export const createVigboAlbumFail = createAction('vigbo/createVigboAlbumFail');

export const selectItemsFromCloudToVigboAlbum = createAction<VigboAlbumId>('vigbo/selectItemsFromCloudToVigboAlbum');

export const selectVigboAlbumAndAddItems = createAction<SelectVigboAlbumAndAddItemsPayload>('vigbo/selectVigboAlbumAndAddItems');

export const updateVigboAccountLinkState = createAction<VigboLinkState>('vigbo/updateVigboAccountLinkState');

export const vigboReducer = createReducer(initialState, {
    [loadVigboAlbumsListRequest.type]: (state) => {
        state.isLoaded = false;
        state.isLoading = true;
    },
    [loadVigboAlbumsListFail.type]: (state) => {
        state.isLoading = false;
    },
    [loadVigboAlbumsListSuccess.type]: (state, action: ReturnType<typeof loadVigboAlbumsListSuccess>) => {
        state.isLoading = false;
        state.isLoaded = true;

        const { list } = action.payload;
        const mappedItems = list.reduce((acc, item) => {
            acc[item.id] = normalizeVigboAlbum(item);
            return acc;
        }, {});
        state.list = mappedItems;
    },
    [loadVigboAlbumRequest.type]: (state, action: ReturnType<typeof loadVigboAlbumRequest>) => {
        const { albumId } = action.payload;

        /** Делаем проверку для приведения типа */
        const album = state.list[albumId];
        if (isAlbum(album)) {
            state.list[albumId] = { ...album, isLoaded: false, isLoading: true };
        }
    },
    [loadVigboAlbumSuccess.type]: (state, action: ReturnType<typeof loadVigboAlbumSuccess>) => {
        const { list, albumId } = action.payload;
        const album = state.list[albumId];

        if (album && album.type !== 'album') {
            return;
        }

        state.list[albumId] = normalizeVigboAlbum({ ...album, ...action.payload, id: albumId, isLoaded: true, isLoading: false });
        list.forEach((item) => {
            const albumItem = normalizeVigboAlbumItem(item);
            state.list[albumItem.id] = albumItem;

            /**
             * Typescript почему-то отказывается определять тип state.list[albumId] внутри функции,
             * хотя во вне все работает корректно, поэтому кастуем тип
             */
            (state.list[albumId] as IAlbum).childs.push(albumItem.id);
        });
    },
    [loadVigboAlbumFail.type]: (state, action: ReturnType<typeof loadVigboAlbumFail>) => {
        const { albumId } = action.payload;

        state.list[albumId] = {
            id: albumId,
            name: '',
            storage: EStorageType.vigbo,
            kind: 'album',
            type: 'album',
            isFolder: true,
            childs: [],
            parent: ROOT_FOLDER_ID,
            isLoaded: false,
            isLoading: true,
        };
    },
    [createVigboAlbumSuccess.type]: (state, action: ReturnType<typeof createVigboAlbumSuccess>) => {
        const { id, title, photos_count } = action.payload;

        state.list[id] = {
            id,
            name: title,
            photosCount: photos_count,
            storage: EStorageType.vigbo,
            kind: 'album',
            type: 'album',
            isFolder: true,
            childs: [],
            parent: ROOT_FOLDER_ID,
        };
    },
    [updateVigboAccountLinkState.type]: (state, action: ReturnType<typeof updateVigboAccountLinkState>) => {
        state.linkState = action.payload.linkState;
    },
    [removeFileSuccess.type]: (state, action: ReturnType<typeof removeFileSuccess>) => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];
            const parent = state.list[item?.parent || ''];

            if (item) {
                delete state.list[id];
            }

            if (parent && isAlbum(parent)) {
                parent.childs = parent.childs.filter((child) => child !== id);
            }
        });
    },
    [renameItemSuccess.type]: (state, action: ReturnType<typeof renameItemSuccess>) => {
        const {
            newItem: { name },
            oldId: id,
        } = action.payload;

        const item = state.list[id];

        if (!item) {
            return;
        }

        state.list[id] = {
            ...item,
            name,
        };
    },
});
